import { useEffect, useRef, useState } from "react";
import { basicSetup } from "codemirror";
import { EditorState } from "@codemirror/state";
import { EditorView } from "@codemirror/view";
import { json, jsonParseLinter } from "@codemirror/lang-json";
import { linter, lintGutter } from "@codemirror/lint";

type Issue = {
  message: string;
  position: number | null;
  line: number | null;
  column: number | null;
};

function jsonIssue(text: string): Issue | null {
  if (!text.trim()) return null;

  try {
    JSON.parse(text);
    return null;
  } catch (error) {
    const message = error instanceof Error
      ? error.message
      : String(error);

    const offset = /position\s+(\d+)/i.exec(message);
    const location = /line\s+(\d+)\s+column\s+(\d+)/i.exec(message);

    let position: number | null = offset
      ? Number(offset[1])
      : null;

    if (position === null && location) {
      const lines = text.split("\n");
      const line = Number(location[1]);
      const column = Number(location[2]);

      if (line >= 1 && line <= lines.length) {
        position = lines.slice(0, line - 1)
          .reduce((total, part) => total + part.length + 1, 0)
          + column - 1;
      }
    }

    if (position === null) {
      return { message, position: null, line: null, column: null };
    }

    position = Math.max(0, Math.min(text.length, position));

    const preceding = text.slice(0, position).split("\n");

    return {
      message,
      position,
      line: preceding.length,
      column: (preceding[preceding.length - 1] ?? "").length + 1
    };
  }
}

export function ProposalJsonEditor({
  value,
  onChange
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const mount = useRef<HTMLDivElement>(null);
  const editor = useRef<EditorView | null>(null);
  const changingFromParent = useRef(false);
  const onChangeRef = useRef(onChange);
  const [issue, setIssue] = useState<Issue | null>(
    () => jsonIssue(value)
  );

  onChangeRef.current = onChange;

  useEffect(() => {
    if (!mount.current) return;

    const view = new EditorView({
      parent: mount.current,
      state: EditorState.create({
        doc: value,
        extensions: [
          basicSetup,
          json(),
          EditorView.lineWrapping,
          lintGutter(),
          linter(jsonParseLinter(), { delay: 250 }),
          EditorView.contentAttributes.of({
            "aria-label": "Proposal JSON editor"
          }),
          EditorView.updateListener.of(update => {
            if (!update.docChanged || changingFromParent.current) {
              return;
            }

            const next = update.state.doc.toString();

            setIssue(jsonIssue(next));
            onChangeRef.current(next);
          })
        ]
      })
    });

    editor.current = view;

    return () => {
      editor.current = null;
      view.destroy();
    };
  }, []);

  useEffect(() => {
    const view = editor.current;
    setIssue(jsonIssue(value));

    if (!view || view.state.doc.toString() === value) return;

    changingFromParent.current = true;

    try {
      view.dispatch({
        changes: {
          from: 0,
          to: view.state.doc.length,
          insert: value
        }
      });
    } finally {
      changingFromParent.current = false;
    }
  }, [value]);

  const goToError = () => {
    const view = editor.current;
    if (!view || issue?.position === null ||
        issue?.position === undefined) return;

    view.dispatch({
      selection: { anchor: issue.position },
      scrollIntoView: true
    });

    view.focus();
  };

  const formatJson = () => {
    if (issue || !value.trim()) return;

    const formatted = JSON.stringify(JSON.parse(value), null, 2);

    if (formatted !== value) onChange(formatted);
  };

  return (
    <div className="db-proposal-editor">
      <div ref={mount} className="db-proposal-editor-surface" />

      <div className="db-proposal-editor-toolbar">
        <span role="status">
          {!value.trim()
            ? "Paste or load JSON to begin"
            : issue
              ? `Invalid JSON${issue.line
                  ? ` at line ${issue.line}, column ${issue.column}`
                  : ""}`
              : "Valid JSON syntax"}
        </span>

        <button
          type="button"
          disabled={!issue || issue.position === null}
          onClick={goToError}
        >
          Go to error
        </button>

        <button
          type="button"
          disabled={!!issue || !value.trim()}
          onClick={formatJson}
          title="Explicitly formats parsed JSON. Duplicate object keys may be collapsed."
        >
          Format JSON
        </button>
      </div>

      {issue && (
        <p role="alert" className="db-proposal-editor-error">
          {issue.message}
        </p>
      )}
    </div>
  );
}
